import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { sheetsService } from '@/services/sheets/sheetsService';
import { GOALS } from '@/constants';

interface PeriodCounts {
  today: number;
  week: number;
  month: number;
}

interface DashboardStats {
  referidos: PeriodCounts;
  completos: PeriodCounts;
  incompletos: PeriodCounts;
  citas: PeriodCounts;
  ventas: PeriodCounts;
  montoVentas: PeriodCounts;
}

const MOTIVA = [
  '¡Cada contacto te acerca a tu próximo regalo!',
  'Más contactos hoy = más ventas mañana. ¡Sigue así!',
  '20 contactos y se gana un regalo. ¡Tú puedes!',
  '¡Vas con todo! Un contacto más, y luego otro.',
  'Tu constancia hace la diferencia. ¡Imparable!',
];

const ICON_USERS =
  'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z';
const ICON_CHECK = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
const ICON_CLOCK = 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z';
const ICON_BOLT = 'M13 10V3L4 14h7v7l9-11h-7z';
const ICON_MONEY =
  'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z';

const emptyCounts: PeriodCounts = { today: 0, week: 0, month: 0 };

const formatMoney = (n: number) => '$' + Math.round(n).toLocaleString('en-US');

export function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    referidos: emptyCounts,
    completos: emptyCounts,
    incompletos: emptyCounts,
    citas: emptyCounts,
    ventas: emptyCounts,
    montoVentas: emptyCounts,
  });
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [msgIndex, setMsgIndex] = useState(() => Math.floor(Math.random() * MOTIVA.length));

  useEffect(() => {
    const loadStats = async () => {
      if (!user) return;
      try {
        const data = await sheetsService.getDashboardStats(user.email, user.name);
        setStats({
          referidos: data.referidos,
          completos: data.completos,
          incompletos: data.incompletos,
          citas: data.citas,
          ventas: data.ventas,
          montoVentas: data.montoVentas,
        });
        setSpreadsheetId(data.spreadsheetId);
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user]);

  useEffect(() => {
    const id = setInterval(() => {
      setMsgIndex((i) => (i + 1) % MOTIVA.length);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  const sheetUrl = spreadsheetId
    ? `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit?usp=sharing`
    : null;

  const copyLink = useCallback(async () => {
    if (!sheetUrl) return;
    try {
      await navigator.clipboard.writeText(sheetUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = sheetUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [sheetUrl]);

  const todayProgress = Math.min((stats.referidos.today / GOALS.daily) * 100, 100);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Tus Estadísticas</h2>
        <p className="text-sm text-gray-500">
          {user?.name} -{' '}
          {new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Hero: Programa de hoy */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 text-white">
        <p className="text-primary-100 text-sm">Programa de hoy</p>
        <div className="flex items-baseline gap-1 mb-3">
          <span className="text-3xl font-bold">{stats.referidos.today}</span>
          <span className="text-primary-100">/ {GOALS.daily}</span>
        </div>
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-white rounded-full transition-all duration-500"
            style={{ width: `${todayProgress}%` }}
          />
        </div>
        <p className="text-xs text-primary-100 mt-2">
          {stats.referidos.today >= GOALS.daily
            ? '¡Meta cumplida! Excelente trabajo.'
            : `Te faltan ${GOALS.daily - stats.referidos.today} contactos para tu meta de hoy.`}
        </p>
      </div>

      {/* Ventas a clientes (monto + numero de ventas) */}
      <VentasCard ventas={stats.ventas} montoVentas={stats.montoVentas} />

      {/* Stat cards */}
      <StatCard
        title="Referidos capturados"
        counts={stats.referidos}
        chipBg="bg-blue-100"
        chipText="text-blue-600"
        iconPath={ICON_USERS}
      />
      <StatCard
        title="Programas completos (20+)"
        counts={stats.completos}
        chipBg="bg-green-100"
        chipText="text-green-600"
        iconPath={ICON_CHECK}
      />
      <StatCard
        title="Programas incompletos (menos de 20)"
        counts={stats.incompletos}
        chipBg="bg-orange-100"
        chipText="text-orange-600"
        iconPath={ICON_CLOCK}
      />
      <StatCard
        title="Citas instantáneas"
        counts={stats.citas}
        chipBg="bg-amber-100"
        chipText="text-amber-600"
        iconPath={ICON_BOLT}
      />

      {/* Metas de referidos */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Metas de referidos</h3>
        <MetaBar label="Esta semana" value={stats.referidos.week} goal={GOALS.weekly} />
        <div className="h-4" />
        <MetaBar label="Este mes" value={stats.referidos.month} goal={GOALS.monthly} />
      </div>

      {/* Google Sheet */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-green-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z" />
          </svg>
          <h3 className="font-semibold text-gray-900">Tu Base de Datos</h3>
        </div>

        {sheetUrl ? (
          <>
            <div className="flex items-center gap-2">
              <a
                href={sheetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium text-sm py-2.5 px-4 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Abrir Google Sheet
              </a>
              <button
                onClick={copyLink}
                className="p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                title="Copiar enlace"
              >
                {linkCopied ? (
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )}
              </button>
            </div>
            {linkCopied && (
              <p className="text-xs text-green-600 mt-2 text-center font-medium">Enlace copiado</p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">Se creará automáticamente con tu primera captura</p>
        )}
      </div>

      {/* Mensaje motivacional (rota cada 6s) */}
      <div className="bg-primary-50 rounded-xl p-4 text-center">
        <p className="text-primary-800 font-medium">{MOTIVA[msgIndex]}</p>
      </div>
    </div>
  );
}

interface PeriodProps {
  label: string;
  value: number;
}

function Period({ label, value }: PeriodProps) {
  return (
    <div className="flex-1 text-center">
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

interface StatCardProps {
  title: string;
  counts: PeriodCounts;
  chipBg: string;
  chipText: string;
  iconPath: string;
}

function StatCard({ title, counts, chipBg, chipText, iconPath }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${chipBg}`}>
          <svg className={`w-5 h-5 ${chipText}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
          </svg>
        </div>
        <span className="font-medium text-gray-900 text-sm">{title}</span>
      </div>
      <div className="flex">
        <Period label="Hoy" value={counts.today} />
        <Period label="Semana" value={counts.week} />
        <Period label="Mes" value={counts.month} />
      </div>
    </div>
  );
}

interface VentaPeriodProps {
  label: string;
  monto: number;
  count: number;
}

function VentaPeriod({ label, monto, count }: VentaPeriodProps) {
  return (
    <div className="flex-1 text-center">
      <p className="text-lg font-bold text-gray-900">{formatMoney(monto)}</p>
      <p className="text-xs text-gray-500">
        {count} venta{count !== 1 ? 's' : ''} · {label}
      </p>
    </div>
  );
}

interface VentasCardProps {
  ventas: PeriodCounts;
  montoVentas: PeriodCounts;
}

function VentasCard({ ventas, montoVentas }: VentasCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-green-100">
          <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICON_MONEY} />
          </svg>
        </div>
        <span className="font-medium text-gray-900 text-sm">Ventas a clientes</span>
      </div>
      <div className="flex">
        <VentaPeriod label="Hoy" monto={montoVentas.today} count={ventas.today} />
        <VentaPeriod label="Semana" monto={montoVentas.week} count={ventas.week} />
        <VentaPeriod label="Mes" monto={montoVentas.month} count={ventas.month} />
      </div>
    </div>
  );
}

interface MetaBarProps {
  label: string;
  value: number;
  goal: number;
}

function MetaBar({ label, value, goal }: MetaBarProps) {
  const pct = Math.min((value / goal) * 100, 100);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-500">
          {value} / {goal}
        </span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            pct >= 100 ? 'bg-success-500' : 'bg-primary-500'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
